import { css } from '@emotion/react';
import styled from '@emotion/styled';
import Icon from '@mdi/react';
import { Popover, Variables } from '../base';
import {
    BADGE_ICON_SIZE,
    metricBadgeBaseStyles,
    metricBadgeHoverStyles,
    metricBadgeLabeledValueHoverStyles,
    metricBadgeTokens,
} from '../badge/entity-badge.styles';

export type HeaderChipTone = 'neutral' | 'attention';

export const FUTURE_RUN_NOTE =
    'Suppression applies to future coder runs. It does not rerun coding or undo previous changes.';

export const DialogBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
    box-sizing: border-box;
    padding: 8px 22px 22px;
`;

export const Note = styled.p`
    margin: 0;
    font-family: ${Variables.Fonts.calibre};
    font-size: 13px;
    line-height: 1.4;
    color: ${Variables.ThemeLight.TextMild};
`;

export const Status = styled.p`
    margin: 0;
    font-family: ${Variables.Fonts.calibre};
    font-size: 14px;
    line-height: 1.4;
    color: ${Variables.ThemeLight.TextMedium};
`;

export const ErrorText = styled.p`
    margin: 0;
    font-family: ${Variables.Fonts.calibre};
    font-size: 14px;
    line-height: 1.4;
    color: ${Variables.ThemeLight.AttentionInverted3};
`;

export const DropdownError = styled(ErrorText)`
    padding: 0 16px 8px;
`;

export const Actions = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 8px;
`;

export const FailureList = styled.ul`
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 240px;
    overflow: auto;
`;

export const FailureItem = styled.li`
    padding: 8px 10px;
    border-radius: 6px;
    border: 1px solid ${Variables.ThemeLight.Border1};
    background: ${Variables.ThemeLight.Background1};
    color: ${Variables.ThemeLight.TextMedium};
    font-family: ${Variables.Fonts.calibre};
    font-size: 13px;
    line-height: 1.4;
`;

// Floats above the page like the equivalent selection-driven Fab elsewhere in the app
// (see `enter-support/.../claims-list/actions.tsx`'s `S.ActionsContainer`), rather than sitting
// inline in the table's own toolbar row.
export const FabContainer = styled.div`
    position: fixed;
    right: 50px;
    bottom: 50px;
    z-index: 1;
`;

export const FabPopover = styled(Popover)`
    display: inline-flex;
`;

export const FabMenuLabel = styled.div`
    padding: 8px 16px;
    font-family: ${Variables.Fonts.calibre};
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: ${Variables.ThemeLight.TextMedium};
    white-space: nowrap;
`;

export const HeaderChipPopover = styled(Popover)`
    display: inline-flex;
    align-items: center;
`;

export const HeaderChipMain = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 7px;
    min-width: 0;
    height: 100%;
    padding: 0 12px;
    white-space: nowrap;
`;

export const HeaderChipAction = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    align-self: stretch;
    flex-shrink: 0;
    padding: 0 8px;
    border-left: 1px solid ${metricBadgeTokens.border};
`;

const attentionChipStyles = css`
    border-color: ${Variables.ThemeLight.AttentionMild3};
    background-color: ${Variables.ThemeLight.AttentionMild3};
    color: ${Variables.ThemeLight.AttentionInverted3};

    ${HeaderChipMain} span {
        color: inherit;
    }

    ${HeaderChipAction} {
        border-left-color: ${Variables.ThemeLight.AttentionMedium1};
    }

    &:hover {
        border-color: ${Variables.ThemeLight.AttentionMild2};
        background-color: ${Variables.ThemeLight.AttentionMild2};
        color: ${Variables.ThemeLight.AttentionInverted3};
    }
`;

const neutralChipStyles = css`
    ${metricBadgeHoverStyles}
    ${metricBadgeLabeledValueHoverStyles}

    &:hover ${HeaderChipAction} {
        border-left-color: ${metricBadgeTokens.borderHover};
    }
`;

export const HeaderChip = styled.button<{ tone: HeaderChipTone }>`
    ${metricBadgeBaseStyles}
    padding: 0;
    gap: 0;
    overflow: hidden;
    justify-content: space-between;

    ${({ tone }) => (tone === 'attention' ? attentionChipStyles : neutralChipStyles)}
`;

export const HeaderChipIcon = styled(Icon)`
    width: ${BADGE_ICON_SIZE};
    height: ${BADGE_ICON_SIZE};
    flex-shrink: 0;
    fill: currentColor;
`;

export const HeaderChipChevron = styled(Icon)`
    width: ${BADGE_ICON_SIZE};
    height: ${BADGE_ICON_SIZE};
    flex-shrink: 0;
    fill: currentColor;
`;

export const Dropdown = styled.div`
    display: flex;
    flex-direction: column;
    width: 420px;
    max-width: calc(100vw - 32px);
    background: ${Variables.ThemeLight.Background1};
    color: ${Variables.ThemeLight.TextMedium};
`;

export const DropdownHeader = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 16px 8px;
`;

export const DropdownTitle = styled.h2`
    margin: 0;
    font-family: ${Variables.Fonts.calibre};
    font-size: 12px;
    font-weight: 600;
    line-height: 1;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: ${Variables.ThemeLight.TextMedium};
`;

export const DropdownCounts = styled.span`
    font-family: ${Variables.Fonts.calibre};
    font-size: 12px;
    color: ${Variables.ThemeLight.TextMild};
`;

export const RuleList = styled.ul`
    margin: 0;
    padding: 0;
    list-style: none;
    max-height: 320px;
    overflow: auto;
`;

export const LoadingRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 12px 8px;
`;

export const RuleRow = styled.li`
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 10px 16px;
`;

export const RuleCopy = styled.div`
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

export const RuleName = styled.span`
    font-family: ${Variables.Fonts.calibre};
    font-size: 14px;
    font-weight: 600;
    color: ${Variables.ThemeLight.TextStrong};
`;

export const RuleDescription = styled.span`
    font-family: ${Variables.Fonts.calibre};
    font-size: 13px;
    line-height: 1.35;
    color: ${Variables.ThemeLight.TextMild};
`;

export const DropdownFooter = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 16px;
    border-top: 1px solid ${Variables.ThemeLight.Border1};
`;

export const FooterNote = styled.p`
    margin: 0;
    font-family: ${Variables.Fonts.calibre};
    font-size: 12px;
    line-height: 1.35;
    color: ${Variables.ThemeLight.TextMild};
`;
